import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Grid } from '@mui/material';
import { usePhrase } from '@/hooks';
import { useServerErrors } from '@/hooks/forms';
import { usePaymentRequestStartData } from '@/hooks/queries';
import { WalletPaymentRequestFormSkeleton } from './WalletPaymentRequestFormSkeleton';
import { IWalletStartPaymentFormDetails, IAccount, IWalletPaymentAllDetails } from '@/types/types';
import { WALLET_REQUEST_PHRASES } from '@/consts/phrases/walletPhrases';
import { ErrorAlert } from '@/components/forms/alerts/ErrorAlert/ErrorAlert';
import { WalletPaymentMethodSelector } from '@/components/blocks/wallet/Payment/WalletPaymentMethodSelector/WalletPaymentMethodSelector';
import { WalletPaymentAmount } from '@/components/blocks/wallet/Payment/WalletPaymentAmount/WalletPaymentAmount';
import { walletFormModel } from '@/components/forms/forms/wallet/walletFormModel';
import { WalletPaymentSteps } from '@/types/enums/forms/wallet/walletPaymentSteps';
import { walletService } from '@/services/api/walletService/walletService';
import { getFetchErrors } from '@/utils/getFetchErrorMessage';
import { isEmpty } from '@/utils/fp';
import { ConfirmDialog } from '@/components/blocks/ConfirmDialog/ConfirmDialog';
import { FormGridErrors } from '@/components/forms/alerts/FormGridErrors/FormGridErrors';

interface WalletPaymentRequestFormProps {
    data: IWalletPaymentAllDetails;
    selectedAccount: IAccount;
    updateWalletPaymentData: (data: Partial<IWalletPaymentAllDetails>) => void;
    clearPaymentDocuments: () => void;
}

export const WalletPaymentRequestForm = ({
    data,
    selectedAccount,
    updateWalletPaymentData,
    clearPaymentDocuments,
}: WalletPaymentRequestFormProps) => {
    const { t } = usePhrase();
    const initialCachedData = data?.cachedData[WalletPaymentSteps.PaymentStart];

    const { paymentRequestStartData: initialData, error: initialDataError } =
        usePaymentRequestStartData(selectedAccount?.accountId);

    const {
        paymentMethodId: paymentMethodIdModel,
        existingPayment: existingPaymentModel,
        lastPaymentMethodId: lastPaymentMethodIdModel,
        lastPayment: lastPaymentModel,
    } = walletFormModel[WalletPaymentSteps.PaymentStart];

    const [requestSeparatePaymentPopupVisible, setRequestSeparatePaymentPopupVisible] =
        useState(false);

    const existingPaymentExists =
        initialCachedData?.existingPayment ?? Boolean(initialData?.existingPayment?.createDt);

    const [paymentInfoExist, setPaymentInfoExist] = useState(existingPaymentExists);
    const [paymentInfoSetManually, setPaymentInfoSetManually] = useState(false);

    if (existingPaymentExists !== paymentInfoExist && !paymentInfoSetManually)
        setPaymentInfoExist(existingPaymentExists);

    const initialFormValues = !isEmpty(initialCachedData)
        ? initialCachedData
        : {
              lastPaymentMethodId: !initialData?.existingPayment?.paymentMethodId
                  ? initialData?.lastPayment?.paymentMethodId
                  : undefined,
              paymentMethodId: initialData?.existingPayment?.paymentMethodId ?? undefined,
              amount:
                  selectedAccount?.balanceNonTaxable && selectedAccount.balanceNonTaxable > 0
                      ? selectedAccount.balanceNonTaxable
                      : undefined,
              lastPayment: initialData?.existingPayment?.createDt
                  ? false
                  : Boolean(initialData?.lastPayment?.paymentMethodId),
              existingPayment: Boolean(initialData?.existingPayment?.createDt),
          };

    const { handleSubmit, control, formState, setValue, getValues, setError, clearErrors } =
        useForm<Partial<IWalletStartPaymentFormDetails>>({
            values: initialFormValues,
        });

    const { errors, isSubmitting } = formState;

    const { handleServerErrors, serverErrors } = useServerErrors({
        setError,
        clearErrors,
        getValues,
    });

    const [paymentDataToChange, setPaymentDataToChange] = useState<{
        paymentId: number;
        field: keyof IWalletStartPaymentFormDetails;
    } | null>(null);

    const handleOpenRequestDialog = (
        paymentId?: number,
        field?: keyof IWalletStartPaymentFormDetails,
    ) => {
        setRequestSeparatePaymentPopupVisible(true);
        if (paymentId && field) setPaymentDataToChange({ paymentId, field });
    };

    const handleCloseRequestDialog = () => {
        setRequestSeparatePaymentPopupVisible(false);
    };

    const handleRequestSeparatePayment = () => {
        setValue(existingPaymentModel.name, false);
        setPaymentInfoSetManually(true);
        setPaymentInfoExist(false);
        if (paymentDataToChange) {
            setValue(paymentDataToChange.field, paymentDataToChange.paymentId);
            if (paymentDataToChange.field === lastPaymentMethodIdModel.name) {
                setValue(paymentMethodIdModel.name, undefined);
                setValue(lastPaymentModel.name, true);
            }
        }
        handleCloseRequestDialog();
    };

    const getInitialError = (error: unknown) => {
        if (!error) return;
        const errorData = getFetchErrors(error);
        return errorData;
    };

    const handleSubmitData = async (vals: Partial<IWalletStartPaymentFormDetails>) => {
        const formData = {
            paymentMethodId: (vals.lastPaymentMethodId || vals.paymentMethodId) ?? null,
            amount: vals.amount ?? null,
            lastPayment: vals.lastPayment,
            existingPayment: vals.existingPayment,
        };
        const paymentMethodData =
            !isEmpty(initialData) && initialData?.paymentMethods
                ? initialData.paymentMethods.find(
                      (item) => String(item.paymentMethodId) === String(formData.paymentMethodId),
                  )
                : null;
        try {
            if (!selectedAccount?.accountId) {
                throw new Error('Account id is not provided');
            }
            const responseData = await walletService.startPaymentRequest(
                selectedAccount.accountId,
                formData,
            );
            const { nextStep, ...currentStepData } = responseData;

            updateWalletPaymentData({
                prevStep: WalletPaymentSteps.PaymentStart,
                currentStep: nextStep,
                cachedData: {
                    ...data.cachedData,
                    [WalletPaymentSteps.PaymentStart]: {
                        ...vals,
                        paymentMethodPhraseId: paymentMethodData?.phraseId ?? null,
                        existingPaymentAmount: vals?.existingPayment
                            ? (initialData?.existingPayment?.amount ?? null)
                            : null,
                    },
                    [nextStep]: {},
                },
                data: { ...data.data, [nextStep]: { ...currentStepData } },
            });
            clearPaymentDocuments();
        } catch (err) {
            handleServerErrors(err);
        }
    };

    return (
        <>
            <ConfirmDialog
                title={t(WALLET_REQUEST_PHRASES.SEPARATE_PAYMENT_QUESTION).toString()}
                actionText={t(WALLET_REQUEST_PHRASES.CONFIRM_BUTTON)}
                cancelText={t(WALLET_REQUEST_PHRASES.CANCEL_BUTTON)}
                open={requestSeparatePaymentPopupVisible}
                handleClose={handleCloseRequestDialog}
                actionHandler={handleRequestSeparatePayment}
            >
                {t(WALLET_REQUEST_PHRASES.SEPARATE_PAYMENT_POPUP_DESCRIPTION)}
            </ConfirmDialog>

            {initialDataError && (
                <Grid size={12} tabIndex={0}>
                    <ErrorAlert
                        id="initial_error_message_box"
                        errorMessage={getInitialError(initialDataError)}
                    />
                </Grid>
            )}
            {initialData && !initialDataError ? (
                <form onSubmit={handleSubmit(handleSubmitData)} noValidate>
                    <Grid container spacing={2}>
                        <FormGridErrors
                            errors={serverErrors}
                            gridSx={{ pb: 4 }}
                            generalErrorId="server_error_message_box"
                        />
                        <Grid size={12}>
                            <WalletPaymentMethodSelector
                                control={control}
                                methodsData={initialData?.paymentMethods}
                                lastPayment={initialData?.lastPayment}
                                errors={errors}
                                setValue={setValue}
                                getValues={getValues}
                                paymentInfoExist={paymentInfoExist}
                                handleOpenRequestDialog={handleOpenRequestDialog}
                                currencyCode={selectedAccount?.currencyCode}
                                currencySymbol={selectedAccount?.currencySymbol}
                                currencyPosition={selectedAccount?.currencySymbolPosition}
                            />
                        </Grid>
                        <Grid size={12}>
                            <WalletPaymentAmount
                                control={control}
                                taxableSum={selectedAccount?.balanceTaxable}
                                currencyCode={selectedAccount?.currencyCode}
                                currencySymbol={selectedAccount?.currencySymbol}
                                currencyPosition={selectedAccount?.currencySymbolPosition}
                                nonTaxableSum={selectedAccount?.balanceNonTaxable}
                                existingPaymentDt={initialData?.existingPayment?.createDt}
                                errors={errors}
                                setValue={setValue}
                                isSubmitting={isSubmitting}
                                paymentInfoExist={paymentInfoExist}
                                handleOpenRequestDialog={handleOpenRequestDialog}
                            />
                        </Grid>
                    </Grid>
                </form>
            ) : (
                !initialDataError && <WalletPaymentRequestFormSkeleton />
            )}
        </>
    );
};
